import { useEffect, useState, useRef } from "react";
import { useModals } from "../common/ModalsProvider.js";
import { DueDateField } from "../form/DueDateField.js";
import { useIsMobile } from "../../hooks/useIsMobile.js";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { abrirArquivo } from "../../lib/open-file.js";
import { trpc } from "../../lib/trpc.js";
import { createPortal } from "react-dom";
import { SendEmailModal } from "./SendInvoiceEmailModal.js";

/**
 * CEO 2026-10-27 — RASCUNHO de fatura com CONFERÊNCIA AUTOMÁTICA (modelo
 * Indy): resumo com os valores atuais dos dossiers, a lista de ✓/⚠/⛔, o PDF
 * do rascunho (marca "BROUILLON", nada é gravado) e "Valider et émettre".
 * No computador o PDF aparece ao lado, já gerado ao abrir; no celular, botão.
 */

type Nivel = "ok" | "info" | "aviso" | "bloqueio";
interface ItemConf {
  nivel: Nivel;
  codigo: string;
  placa?: string | null;
  valor?: number;
  valor2?: number;
  extra?: string[];
}

const ICONE: Record<Nivel, string> = { ok: "✓", info: "ℹ", aviso: "⚠", bloqueio: "⛔" };
const COR: Record<Nivel, React.CSSProperties> = {
  ok: { background: "#DCFCE7", borderColor: "#16A34A", color: "#14532D" },
  info: { background: "#E0F2FE", borderColor: "#0284C7", color: "#0C4A6E" },
  aviso: { background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" },
  bloqueio: { background: "#FEE2E2", borderColor: "#DC2626", color: "#7F1D1D" },
};

export function InvoiceDraftModal({
  companyId,
  draftId,
  onClose,
  onIssued,
  semEnvioAqui = false,
}: {
  companyId: number;
  draftId: number;
  onClose: () => void;
  onIssued?: (invoiceId: number, invoiceNumber: string) => void;
  /** A página Nova fatura já envia pelo próprio campo de e-mail (e fecha o rascunho). */
  semEnvioAqui?: boolean;
}) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const q = trpc.invoiceDrafts.get.useQuery({ companyId, id: draftId });
  // F-conta-na-fatura (CEO 2026-10-31) — em qual conta o cliente paga.
  const contasQ = trpc.customerInvoices.bankAccountOptions.useQuery({ companyId });
  const contasAtivas = (contasQ.data?.rows ?? []).filter((c) => c.active);
  const [numero, setNumero] = useState("");
  const [venc, setVenc] = useState("");
  const [notas, setNotas] = useState("");
  const [carregado, setCarregado] = useState(false);
  const [aceito, setAceito] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [emitida, setEmitida] = useState<string | null>(null);
  // CEO 2026-11-01 — "Émettre et envoyer": emite e já abre a janela de envio.
  const [emitidaId, setEmitidaId] = useState<number | null>(null);
  const [envio, setEnvio] = useState<{ id: number; invoiceNumber: string } | null>(null);
  const [enviadaPara, setEnviadaPara] = useState<string | null>(null);
  const enviarDepois = useRef(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfErro, setPdfErro] = useState<string | null>(null);
  const celular = useIsMobile(1023);

  useEffect(() => {
    if (!q.data || carregado) return;
    setNumero(q.data.draft.invoiceNumber ?? "");
    setVenc(q.data.draft.dueAt ?? "");
    setNotas(q.data.draft.notes ?? "");
    setCarregado(true);
  }, [q.data, carregado]);

  const refrescar = () => {
    void utils.invoiceDrafts.invalidate();
    void utils.customerInvoices.invalidate();
    void utils.dossiers.invalidate();
  };
  const salvar = trpc.invoiceDrafts.update.useMutation({
    onSuccess: () => {
      setErro(null);
      void utils.invoiceDrafts.get.invalidate();
    },
    onError: (e) => setErro(e.message),
  });
  const pdf = trpc.invoiceDrafts.pdf.useMutation();
  // Prévia ao lado (computador): gera ao abrir e de novo a cada "Salvar".
  const versao = q.data ? String(q.data.draft.updatedAt) : null;
  useEffect(() => {
    if (celular || versao == null || emitida) return;
    let vivo = true;
    setPdfErro(null);
    pdf
      .mutateAsync({ companyId, id: draftId })
      .then((r) => {
        if (vivo) setPdfUrl(r.url);
      })
      .catch((e: unknown) => {
        if (vivo) setPdfErro(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao, celular]);
  const emitir = trpc.invoiceDrafts.issue.useMutation({
    onSuccess: (r) => {
      setEmitida(r.invoiceNumber);
      setEmitidaId(r.invoiceId);
      refrescar();
      void utils.customerInvoices.unsentSummary.invalidate();
      if (enviarDepois.current && !semEnvioAqui) {
        setEnvio({ id: r.invoiceId, invoiceNumber: r.invoiceNumber });
      }
      enviarDepois.current = false;
      onIssued?.(r.invoiceId, r.invoiceNumber);
    },
    onError: async (e, vars) => {
      // nº digitado que pula a série: confirma e tenta de novo.
      if (e.message.startsWith("NUMBER_JUMP:")) {
        const go = await modals.confirm({
          title: t("invoiceNew.numberJumpTitle"),
          message: t("invoiceDraft.numberJumpRetry"),
          confirmLabel: t("invoiceNew.numberJumpConfirm"),
          tone: "danger",
        });
        if (go) emitir.mutate({ ...vars, allowNumberJump: true });
        return;
      }
      setErro(
        e.message === "DRAFT_WARNINGS"
          ? t("invoiceDraft.errWarnings")
          : e.message === "DRAFT_BLOCKED"
            ? t("invoiceDraft.errBlocked")
            : e.message === "DRAFT_REPLACEMENT_HAS_PAYMENT"
              ? t("invoiceDraft.errReplacementHasPayment")
              : e.message,
      );
    },
  });
  const descartar = trpc.invoiceDrafts.discard.useMutation({
    onSuccess: () => {
      refrescar();
      onClose();
    },
    onError: (e) =>
      setErro(
        e.message === "DRAFT_REPLACEMENT_HAS_PAYMENT"
          ? t("invoiceDraft.errReplacementHasPayment")
          : e.message,
      ),
  });

  const d = q.data;
  const conf = (d?.conferencia ?? []) as ItemConf[];
  const temBloqueio = conf.some((c) => c.nivel === "bloqueio");
  const temAviso = conf.some((c) => c.nivel === "aviso");
  const mudou =
    d != null &&
    (numero.trim() !== (d.draft.invoiceNumber ?? "") ||
      venc !== (d.draft.dueAt ?? "") ||
      notas.trim() !== (d.draft.notes ?? ""));

  const etapa = (c: string) =>
    c === "dsp"
      ? t("invoiceDraft.stepPdr")
      : c === "montage"
        ? t("invoiceDraft.stepMontage")
        : c === "montage_phases"
          ? t("invoiceDraft.stepMontagePhases")
          : c === "delivery"
            ? t("invoiceDraft.stepDelivery")
            : c;
  const m = (v: number | undefined) => (v == null ? "" : formatMoney(v, { decimals: 2 }));
  const texto = (c: ItemConf): string => {
    const p = c.placa ? `${c.placa}: ` : "";
    switch (c.codigo) {
      case "tudo_certo":
        return t("invoiceDraft.cTudoCerto");
      case "clientes_diferentes":
        return t("invoiceDraft.cClientesDiferentes");
      case "ja_faturado":
        return p + t("invoiceDraft.cJaFaturado");
      case "total_zero":
        return p + t("invoiceDraft.cTotalZero");
      case "sem_placa":
        return t("invoiceDraft.cSemPlaca");
      case "reparo_incompleto":
        return `${p}${t("invoiceDraft.cReparoIncompleto")} ${(c.extra ?? []).map(etapa).join(", ")}`;
      case "degarnissage_pendente":
        return `${p}${t("invoiceDraft.cDegarnPre")} ${m(c.valor)} ${t("invoiceDraft.cDegarnSuf")}`;
      case "linha_sem_comissao":
        return `${p}${c.valor ?? 1} ${t("invoiceDraft.cSemComissao")}`;
      case "franquia_nao_informada":
        return p + t("invoiceDraft.cFranquiaNaoInformada");
      case "franquia_particular":
        return `${p}${t("invoiceDraft.cFranquiaPre")} ${m(c.valor)} ${t("invoiceDraft.cFranquiaParticular")}`;
      case "franquia_pro":
        return `${p}${t("invoiceDraft.cFranquiaPre")} ${m(c.valor)} ${t("invoiceDraft.cFranquiaPro")}`;
      case "franquia_excecao":
        return `${p}${t("invoiceDraft.cFranquiaPre")} ${m(c.valor)} ${t("invoiceDraft.cFranquiaExcecao")}`;
      case "valor_vs_devis": {
        const dif = (c.valor2 ?? 0) - (c.valor ?? 0);
        const pct = c.valor ? Math.round((dif / c.valor) * 1000) / 10 : 0;
        return `${p}${t("invoiceDraft.cDevis")} ${m(c.valor)} → ${t("invoiceDraft.cFatura")} ${m(c.valor2)}${Math.abs(dif) >= 0.01 ? ` (${dif > 0 ? "+" : ""}${m(dif)} · ${pct > 0 ? "+" : ""}${pct} %)` : ""}`;
      }
      case "cliente_pro_sem_remise":
        return t("invoiceDraft.cProSemRemise");
      case "cliente_sem_endereco":
        return t("invoiceDraft.cSemEndereco");
      case "cliente_pro_sem_siret_tva":
        return t("invoiceDraft.cProSemSiret");
      case "cliente_sem_email":
        return t("invoiceDraft.cSemEmail");
      case "tva_particular_sem_tva":
        return t("invoiceDraft.cParticularSemTva");
      case "substitui_fatura":
        return `${t("invoiceDraft.cReplaces")} ${c.placa ?? ""}: ${m(c.valor)} → ${c.valor2 != null ? m(c.valor2) : "…"} HT`;
      case "fatura_antiga_paga":
        return `${t("invoiceDraft.cOldPaid")} ${m(c.valor)} HT`;
      case "tecnico_a_receber":
        return `${p}${t("invoiceDraft.cTecnicoAReceber")} ${m(c.valor)}`;
      case "fatura_antiga_com_avoir":
        return `${c.placa ?? ""}: ${t("invoiceDraft.cOldAvoir")} ${m(c.valor)} HT`;
      default:
        return c.codigo;
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
        onClick={onClose}
        data-testid="invoice-draft-modal"
      >
        <div
          className={`w-full rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl space-y-4 ${
            celular || emitida ? "max-w-3xl" : "max-w-7xl"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-neutral-500">
                📝 {t("invoiceDraft.title")}
              </p>
              <h2 className="text-lg font-semibold">{d?.preview.customer?.name ?? "…"}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-700 px-2 py-0.5 text-sm"
              aria-label="×"
            >
              ×
            </button>
          </div>

          {q.isLoading && <p className="text-sm text-neutral-500">…</p>}
          {q.error && <p className="text-sm text-red-500">{q.error.message}</p>}
          {d?.substitui && !emitida && (
            <p
              className="rounded-md border px-3 py-2 text-xs"
              style={{ background: "#FEE2E2", borderColor: "#DC2626", color: "#7F1D1D" }}
              data-testid="draft-replaces"
            >
              ↻ {t("invoiceDraft.replacesPre")} <strong>{d.substitui.invoiceNumber}</strong>{" "}
              {d.substitui.issuedAt
                ? `(${new Date(d.substitui.issuedAt).toLocaleDateString("fr-FR")}) `
                : ""}
              {t("invoiceDraft.replacesCancelled")} · {t("invoiceDraft.replacesOld")}{" "}
              {formatMoney(d.substitui.totalHt, { decimals: 2 })} HT
              {d.substitui.recebidoHt > 0.005 && (
                <>
                  {" "}
                  · {t("invoiceDraft.replacesPayment")}{" "}
                  {formatMoney(d.substitui.recebidoHt, { decimals: 2 })} HT
                </>
              )}
            </p>
          )}

          {emitida ? (
            <div
              className="rounded-md border px-3 py-3 text-sm"
              style={COR.ok}
              data-testid="draft-issued"
            >
              ✓ {t("invoiceDraft.issuedOk")} <strong className="font-mono">{emitida}</strong>
              {enviadaPara ? (
                <span className="ml-2" data-testid="draft-sent-to">
                  · ✉ {t("invoiceDraft.sentToPre")} {enviadaPara}
                </span>
              ) : (
                !semEnvioAqui &&
                emitidaId != null && (
                  <button
                    type="button"
                    onClick={() => setEnvio({ id: emitidaId, invoiceNumber: emitida })}
                    className="ml-3 rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime"
                    data-testid="draft-send-now"
                  >
                    ✉ {t("invoiceDraft.sendNowBtn")}
                  </button>
                )
              )}
            </div>
          ) : (
            d && (
              <div
                className={
                  // CEO 2026-10-29 — PDF com mais espaço (a fatura ficava pequena).
                  celular ? "space-y-4" : "grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-5"
                }
              >
                <div className="min-w-0 space-y-4">
                  <section className="space-y-1.5" data-testid="draft-checks">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t("invoiceDraft.checksTitle")}
                    </h3>
                    {conf.map((c, i) => (
                      <p
                        key={`${c.codigo}-${c.placa ?? ""}-${i}`}
                        className="rounded-md border px-3 py-1.5 text-xs leading-snug"
                        style={COR[c.nivel]}
                      >
                        {ICONE[c.nivel]} {texto(c)}
                      </p>
                    ))}
                  </section>

                  <section>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t("invoiceDraft.summaryTitle")}
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <tbody>
                          {d.preview.items.map((it) => (
                            <tr key={it.dossierId} className="border-t border-neutral-800">
                              <td className="py-1.5 pr-3 font-mono whitespace-nowrap">
                                {it.plate ?? it.dossierCode}
                              </td>
                              <td className="py-1.5 pr-3 text-neutral-400">{it.vehicle ?? ""}</td>
                              <td className="py-1.5 text-right font-mono tabular-nums whitespace-nowrap">
                                {formatMoney(Number(it.faturado), { decimals: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-[11px] text-neutral-500">{t("invoiceDraft.totalHt")}</p>
                        <p className="font-mono font-semibold">
                          {formatMoney(Number(d.preview.totalHt), { decimals: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] text-neutral-500">
                          {d.preview.tvaReverseCharge
                            ? t("invoiceDraft.reverseCharge")
                            : `${t("invoiceDraft.tva")} ${Number(d.preview.tvaRate ?? 0).toFixed(0)} %`}
                        </p>
                        <p className="font-mono">
                          {formatMoney(Number(d.preview.tvaAmount ?? 0), { decimals: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] text-neutral-500">{t("invoiceDraft.totalTtc")}</p>
                        <p className="font-mono font-semibold">
                          {formatMoney(Number(d.preview.totalTtc ?? 0), { decimals: 2 })}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="block text-xs text-neutral-400">
                      {t("invoiceDraft.fieldNumber")}
                      <input
                        value={numero}
                        onChange={(e) => setNumero(e.target.value.toUpperCase())}
                        placeholder={d.preview.suggestedInvoiceNumber ?? ""}
                        className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 font-mono text-sm"
                      />
                    </label>
                    <div className="block text-xs text-neutral-400 sm:col-span-2">
                      {t("invoiceDraft.fieldDue")}
                      {carregado && (
                        <div className="mt-1">
                          <DueDateField
                            value={venc}
                            onChange={setVenc}
                            inputClass="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm"
                            presetLabel={(n) => t(`invoiceNew.dueQuick${n}`)}
                          />
                        </div>
                      )}
                    </div>
                    {contasAtivas.length > 0 && (
                      <label className="block text-xs text-neutral-400 sm:col-span-3">
                        {t("invoiceDraft.fieldBankAccount")}
                        <select
                          value={d.draft.bankAccountId ?? contasQ.data?.defaultId ?? ""}
                          disabled={salvar.isPending || contasAtivas.length < 2}
                          onChange={(e) =>
                            salvar.mutate({
                              companyId,
                              id: draftId,
                              bankAccountId: e.target.value ? Number(e.target.value) : null,
                            })
                          }
                          className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm"
                          data-testid="draft-bank-account"
                        >
                          {contasAtivas.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                              {c.ibanMasked ? ` · ${c.ibanMasked}` : ""}
                              {c.id === contasQ.data?.defaultId
                                ? ` (${t("invoiceDraft.bankDefaultTag")})`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label className="block text-xs text-neutral-400 sm:col-span-3">
                      {t("invoiceDraft.fieldNotes")}
                      <textarea
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        rows={2}
                        className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm"
                      />
                    </label>
                  </section>

                  {erro && <p className="text-sm text-red-500">{erro}</p>}

                  {temAviso && !temBloqueio && (
                    <label className="flex items-start gap-2 text-xs text-neutral-300">
                      <input
                        type="checkbox"
                        checked={aceito}
                        onChange={(e) => setAceito(e.target.checked)}
                        data-testid="draft-ack"
                      />
                      {t("invoiceDraft.ackWarnings")}
                    </label>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-800 pt-3">
                    <button
                      type="button"
                      onClick={() => descartar.mutate({ companyId, id: draftId })}
                      disabled={descartar.isPending}
                      className="rounded-md border border-red-500/40 px-3 py-1.5 text-sm text-red-500 hover:border-red-500"
                      data-testid="draft-discard"
                    >
                      {t("invoiceDraft.discardBtn")}
                    </button>
                    <div className="flex flex-wrap gap-2">
                      {mudou && (
                        <button
                          type="button"
                          onClick={() =>
                            salvar.mutate({
                              companyId,
                              id: draftId,
                              invoiceNumber: numero.trim() || null,
                              dueAt: venc || null,
                              notes: notas.trim() || null,
                            })
                          }
                          disabled={salvar.isPending}
                          className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
                        >
                          {t("invoiceDraft.save")}
                        </button>
                      )}
                      {celular && (
                        <button
                          type="button"
                          onClick={() => {
                            setErro(null);
                            abrirArquivo(
                              async () => (await pdf.mutateAsync({ companyId, id: draftId })).url,
                            ).catch((e: unknown) =>
                              setErro(e instanceof Error ? e.message : String(e)),
                            );
                          }}
                          disabled={pdf.isPending || mudou}
                          title={mudou ? t("invoiceDraft.saveFirst") : undefined}
                          className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm hover:border-lime hover:text-lime disabled:opacity-40"
                          data-testid="draft-pdf"
                        >
                          👁 {pdf.isPending ? "…" : t("invoiceDraft.pdfBtn")}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          enviarDepois.current = false;
                          emitir.mutate({ companyId, id: draftId, acknowledgeWarnings: aceito });
                        }}
                        disabled={emitir.isPending || temBloqueio || mudou || (temAviso && !aceito)}
                        title={mudou ? t("invoiceDraft.saveFirst") : undefined}
                        className={
                          semEnvioAqui
                            ? "rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:opacity-40"
                            : "rounded-md border border-lime px-4 py-1.5 text-sm font-semibold text-[var(--text-strong)] hover:bg-lime/15 disabled:opacity-40"
                        }
                        data-testid="draft-issue"
                      >
                        {emitir.isPending ? "…" : t("invoiceDraft.issueBtn")}
                      </button>
                      {!semEnvioAqui && (
                        <button
                          type="button"
                          onClick={() => {
                            enviarDepois.current = true;
                            emitir.mutate({ companyId, id: draftId, acknowledgeWarnings: aceito });
                          }}
                          disabled={
                            emitir.isPending || temBloqueio || mudou || (temAviso && !aceito)
                          }
                          title={
                            mudou
                              ? t("invoiceDraft.saveFirst")
                              : t("invoiceDraft.issueAndSendTitle")
                          }
                          className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:opacity-40"
                          data-testid="draft-issue-send"
                        >
                          ✉ {emitir.isPending ? "…" : t("invoiceDraft.issueAndSendBtn")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {!celular && (
                  <section className="flex min-w-0 flex-col" data-testid="draft-pdf-preview">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                        👁 {t("invoiceDraft.previewTitle")}
                      </h3>
                      {pdfUrl && (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener"
                          className="text-xs text-neutral-500 underline hover:text-lime"
                        >
                          {t("invoiceDraft.openNewTab")}
                        </a>
                      )}
                    </div>
                    {mudou && (
                      <p className="mb-1 rounded-md border px-3 py-1 text-xs" style={COR.aviso}>
                        {t("invoiceDraft.previewStale")}
                      </p>
                    )}
                    <div className="relative min-h-[70vh] flex-1 overflow-hidden rounded-md border border-neutral-700 bg-neutral-950">
                      {pdfUrl && (
                        <iframe
                          key={pdfUrl}
                          // CEO 2026-10-29 — sem a coluna de miniaturas do leitor de PDF
                          // (ocupava metade da prévia); página na largura toda.
                          src={`${pdfUrl}#navpanes=0&view=FitH`}
                          title={t("invoiceDraft.previewTitle")}
                          className="absolute inset-0 h-full w-full"
                        />
                      )}
                      {(pdf.isPending || (!pdfUrl && !pdfErro)) && (
                        <p className="absolute inset-x-0 top-1/2 text-center text-sm text-neutral-500">
                          {t("invoiceDraft.previewLoading")}
                        </p>
                      )}
                      {pdfErro && (
                        <p className="absolute inset-x-4 top-4 text-sm text-red-500">{pdfErro}</p>
                      )}
                    </div>
                  </section>
                )}
              </div>
            )
          )}
        </div>
      </div>
      {envio &&
        createPortal(
          <SendEmailModal
            companyId={companyId}
            invoice={{ id: envio.id, invoiceNumber: envio.invoiceNumber }}
            onClose={() => setEnvio(null)}
            onSent={(para) => setEnviadaPara(para ?? null)}
          />,
          document.body,
        )}
    </>
  );
}
